import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminConfirmModal from "./AdminConfirmModal";

const AdminUserDetail = () => {

    const { id } = useParams();
    const navigate = useNavigate();

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [actionLoading, setActionLoading] =
        useState(false);

    const [emailModalOpen, setEmailModalOpen] =
        useState(false);

    const [emailBody, setEmailBody] =
        useState("");

    const [emailSending, setEmailSending] =
        useState(false);

    const [emailSuccess, setEmailSuccess] =
        useState("");

    const [confirmModal, setConfirmModal] =
        useState({
            open: false,
            action: null,
            title: "",
            message: "",
            danger: false
        });


    const closeConfirmModal = () => {

        if (actionLoading) {
            return;
        }

        setConfirmModal({
            open: false,
            action: null,
            title: "",
            message: "",
            danger: false
        });
    };


    const requestAction = (
        action,
        title,
        message,
        danger = false
    ) => {

        setConfirmModal({
            open: true,
            action,
            title,
            message,
            danger
        });
    };


    const loadUser = async () => {

        try {

            setLoading(true);
            setError("");

            const token =
                localStorage.getItem(
                    "authToken"
                );

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/admin/users/${id}`,
                {
                    headers: {
                        Authorization:
                            token?.startsWith(
                                "Bearer "
                            )
                                ? token
                                : `Bearer ${token}`
                    }
                }
            );

            if (!response.ok) {

                if (
                    response.status === 401 ||
                    response.status === 403
                ) {
                    throw new Error(
                        "You are not authorized to view this user."
                    );
                }

                throw new Error(
                    "Unable to load user."
                );
            }

            const data =
                await response.json();

            setUser(data);

        } catch (error) {

            console.error(
                "Admin user detail error:",
                error
            );

            setError(
                error.message ||
                "Unable to load user."
            );

        } finally {

            setLoading(false);
        }
    };


    useEffect(() => {

        loadUser();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);


    const sendUserEmail = async () => {

        if (!emailBody.trim()) {
            return;
        }

        try {

            setEmailSending(true);
            setEmailSuccess("");
            setError("");

            const token =
                localStorage.getItem(
                    "authToken"
                );

            const response =
                await fetch(
                    `${process.env.REACT_APP_API_URL}/api/admin/users/${id}/email`,
                    {
                        method: "POST",

                        headers: {
                            "Content-Type":
                                "application/json",

                            Authorization:
                                token?.startsWith(
                                    "Bearer "
                                )
                                    ? token
                                    : `Bearer ${token}`
                        },

                        body: JSON.stringify({
                            message:
                                emailBody.trim()
                        })
                    }
                );

            const data =
                await response
                    .json()
                    .catch(() => ({}));

            if (!response.ok) {

                throw new Error(
                    data.message ||
                    "Unable to send email."
                );
            }

            setEmailSuccess(
                "Email sent successfully."
            );

            setEmailBody("");

            setTimeout(() => {
                setEmailModalOpen(false);
                setEmailSuccess("");
            }, 1200);

        } catch (error) {

            console.error(
                "Admin email error:",
                error
            );

            setError(
                error.message ||
                "Unable to send email."
            );

        } finally {

            setEmailSending(false);
        }
    };

    const updateUserStatus =
        async (action) => {

            if (!action) {
                return;
            }

            try {

                setActionLoading(true);
                setError("");

                const token =
                    localStorage.getItem(
                        "authToken"
                    );

                const response =
                    await fetch(
                        `${process.env.REACT_APP_API_URL}/api/admin/users/${id}/${action}`,
                        {
                            method: "PATCH",
                            headers: {
                                Authorization:
                                    token?.startsWith(
                                        "Bearer "
                                    )
                                        ? token
                                        : `Bearer ${token}`
                            }
                        }
                    );

                if (!response.ok) {

                    const errorData =
                        await response
                            .json()
                            .catch(
                                () => ({})
                            );

                    throw new Error(
                        errorData.message ||
                        `Unable to ${action} user.`
                    );
                }


                const refreshedUser =
                    await fetch(
                        `${process.env.REACT_APP_API_URL}/api/admin/users/${id}`,
                        {
                            headers: {
                                Authorization:
                                    token?.startsWith(
                                        "Bearer "
                                    )
                                        ? token
                                        : `Bearer ${token}`
                            }
                        }
                    );


                if (!refreshedUser.ok) {
                    throw new Error(
                        "User status updated, but profile refresh failed."
                    );
                }


                const data =
                    await refreshedUser.json();

                setUser(data);

                setConfirmModal({
                    open: false,
                    action: null,
                    title: "",
                    message: "",
                    danger: false
                });

            } catch (error) {

                console.error(
                    "Admin user action error:",
                    error
                );

                setError(
                    error.message ||
                    "Unable to update this user."
                );

            } finally {

                setActionLoading(false);
            }
        };


    if (loading) {

        return (
            <div
                style={{
                    minHeight: "100vh",
                    background: "#f8f9fb",
                    padding: "40px"
                }}
            >
                Loading user...
            </div>
        );
    }


    if (error && !user) {

        return (
            <div
                style={{
                    minHeight: "100vh",
                    background: "#f8f9fb",
                    padding: "40px"
                }}
            >

                <button
                    type="button"
                    onClick={() =>
                        navigate(
                            "/admin/users"
                        )
                    }
                >
                    ← Users
                </button>

                <p
                    style={{
                        color: "#ef4444"
                    }}
                >
                    {error}
                </p>

            </div>
        );
    }


    if (!user) {
        return null;
    }


    return (
        <div
            style={{
                width: "100%",
                minHeight: "100vh",
                height: "auto",
                background: "#f8f9fb",
                padding: "32px",
                overflow: "visible"
            }}
        >

            <div
                style={{
                    maxWidth: "1000px",
                    margin: "0 auto"
                }}
            >

                <button
                    type="button"
                    onClick={() =>
                        navigate(
                            "/admin/users"
                        )
                    }
                    style={{
                        marginBottom: "20px",
                        padding: "9px 14px",
                        borderRadius: "9px",
                        border:
                            "1px solid #d1d5db",
                        background: "#fff",
                        cursor: "pointer",
                        fontWeight: "700"
                    }}
                >
                    ← Users
                </button>


                {error && (
                    <div
                        style={{
                            marginBottom: "20px",
                            padding: "14px 16px",
                            background: "#fef2f2",
                            color: "#b91c1c",
                            border:
                                "1px solid #fecaca",
                            borderRadius: "12px"
                        }}
                    >
                        {error}
                    </div>
                )}


                <div
                    style={{
                        background: "#fff",
                        padding: "30px",
                        borderRadius: "20px",
                        boxShadow:
                            "0 8px 30px rgba(15,23,42,0.05)"
                    }}
                >

                    {/* HEADER */}

                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "20px",
                            marginBottom: "28px"
                        }}
                    >

                        <img
                            src={
                                user.mediaUrls?.[0] ||
                                "/default-avatar.png"
                            }
                            alt={
                                user.name ||
                                "User"
                            }
                            style={{
                                width: "95px",
                                height: "95px",
                                borderRadius: "50%",
                                objectFit: "cover",
                                border:
                                    "3px solid #f1f5f9"
                            }}
                        />


                        <div>

                            <h1
                                style={{
                                    margin: 0,
                                    fontSize: "34px"
                                }}
                            >
                                {user.name}
                            </h1>

                            <p
                                style={{
                                    margin:
                                        "7px 0 0",
                                    color: "#64748b"
                                }}
                            >
                                {user.username}
                            </p>


                            <div
                                style={{
                                    marginTop:
                                        "10px",
                                    display:
                                        "flex",
                                    gap: "8px",
                                    flexWrap:
                                        "wrap"
                                }}
                            >

                                {user.banned && (
                                    <StatusBadge
                                        text="Banned"
                                        background="#fee2e2"
                                        color="#b91c1c"
                                    />
                                )}

                                {!user.banned &&
                                    user.suspended && (
                                        <StatusBadge
                                            text="Suspended"
                                            background="#fef3c7"
                                            color="#b45309"
                                        />
                                    )}

                                {!user.banned &&
                                    !user.suspended && (
                                        <StatusBadge
                                            text="Active"
                                            background="#dcfce7"
                                            color="#15803d"
                                        />
                                    )}

                                {user.isOnline && (
                                    <StatusBadge
                                        text="Online"
                                        background="#dbeafe"
                                        color="#1d4ed8"
                                    />
                                )}

                            </div>

                        </div>

                    </div>


                    {/* INFORMATION */}

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns:
                                "repeat(auto-fit, minmax(220px, 1fr))",
                            gap: "18px"
                        }}
                    >

                        <Info
                            label="Age"
                            value={user.age}
                        />

                        <Info
                            label="Gender"
                            value={user.gender}
                        />

                        <Info
                            label="Marital Status"
                            value={
                                user.maritalStatus
                            }
                        />

                        <Info
                            label="Children"
                            value={
                                user.hasChildren
                                    ? "Yes"
                                    : "No"
                            }
                        />

                        <Info
                            label="City"
                            value={user.city}
                        />

                        <Info
                            label="State"
                            value={user.state}
                        />

                        <Info
                            label="Country"
                            value={user.country}
                        />

                        <Info
                            label="Online"
                            value={
                                user.isOnline
                                    ? "Yes"
                                    : "No"
                            }
                        />

                        <Info
                            label="Coins"
                            value={
                                user.coinBalance
                            }
                        />

                        <Info
                            label="Creator Earnings"
                            value={
                                user.creatorEarningsUnits
                            }
                        />

                        <Info
                            label="Suspended"
                            value={
                                user.suspended
                                    ? "Yes"
                                    : "No"
                            }
                        />

                        <Info
                            label="Banned"
                            value={
                                user.banned
                                    ? "Yes"
                                    : "No"
                            }
                        />

                    </div>


                    {/* ADMIN ACTIONS */}

                    <div
                        style={{
                            marginTop: "30px",
                            paddingTop: "25px",
                            borderTop:
                                "1px solid #e5e7eb"
                        }}
                    >

                        <h3
                            style={{
                                margin:
                                    "0 0 15px"
                            }}
                        >
                            Account Actions
                        </h3>


                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "12px"
                            }}
                        >

                            {!user.suspended &&
                                !user.banned && (
                                    <ActionButton
                                        text="Suspend"
                                        background="#f59e0b"
                                        onClick={() =>
                                            requestAction(
                                                "suspend",
                                                "Suspend user?",
                                                `${user.name} will temporarily lose access to MyHalal.`,
                                                false
                                            )
                                        }
                                    />
                                )}


                            {user.suspended &&
                                !user.banned && (
                                    <ActionButton
                                        text="Reactivate"
                                        background="#22c55e"
                                        onClick={() =>
                                            requestAction(
                                                "reactivate",
                                                "Reactivate user?",
                                                `${user.name} will regain access to MyHalal.`,
                                                false
                                            )
                                        }
                                    />
                                )}


                            {!user.banned && (
                                <ActionButton
                                    text="Ban"
                                    background="#ef4444"
                                    onClick={() =>
                                        requestAction(
                                            "ban",
                                            "Ban user?",
                                            `${user.name} will be blocked from accessing MyHalal.`,
                                            true
                                        )
                                    }
                                />
                            )}


                            {user.banned && (
                                <ActionButton
                                    text="Unban"
                                    background="#22c55e"
                                    onClick={() =>
                                        requestAction(
                                            "unban",
                                            "Unban user?",
                                            `${user.name} will be allowed to access MyHalal again.`,
                                            false
                                        )
                                    }
                                />
                            )}


                            <ActionButton
                                text="Email User"
                                background="#ec4899"
                                onClick={() => {
                                    setEmailBody("");
                                    setEmailSuccess("");
                                    setEmailModalOpen(true);
                                }}
                            />

                        </div>

                    </div>


                    {/* BIO */}

                    <div
                        style={{
                            marginTop: "30px",
                            paddingTop: "25px",
                            borderTop:
                                "1px solid #e5e7eb"
                        }}
                    >

                        <h3
                            style={{
                                margin:
                                    "0 0 10px"
                            }}
                        >
                            Bio
                        </h3>

                        <p
                            style={{
                                margin: 0,
                                color: "#64748b",
                                lineHeight: 1.7
                            }}
                        >
                            {user.bio ||
                                "No bio"}
                        </p>

                    </div>

                </div>

            </div>


            <AdminConfirmModal
                open={
                    confirmModal.open
                }
                title={
                    confirmModal.title
                }
                message={
                    confirmModal.message
                }
                danger={
                    confirmModal.danger
                }
                loading={
                    actionLoading
                }
                confirmText={
                    confirmModal.action ===
                        "ban"
                        ? "Ban user"
                        : confirmModal.action ===
                            "suspend"
                            ? "Suspend user"
                            : confirmModal.action ===
                                "unban"
                                ? "Unban user"
                                : "Reactivate user"
                }
                onCancel={
                    closeConfirmModal
                }
                onConfirm={() =>
                    updateUserStatus(
                        confirmModal.action
                    )
                }
            />

            {emailModalOpen && (

    <div
        onClick={() => {
            if (!emailSending) {
                setEmailModalOpen(false);
            }
        }}
        style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,

            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            padding: "20px",

            background:
                "rgba(15,23,42,.58)",

            backdropFilter:
                "blur(5px)"
        }}
    >

        <div
            onClick={(event) =>
                event.stopPropagation()
            }
            style={{
                width: "100%",
                maxWidth: "540px",

                background: "#fff",

                borderRadius: "22px",

                padding: "26px",

                boxShadow:
                    "0 25px 70px rgba(0,0,0,.25)"
            }}
        >

            <div
                style={{
                    display: "flex",
                    justifyContent:
                        "space-between",
                    alignItems:
                        "flex-start",
                    gap: "15px"
                }}
            >

                <div>

                    <h2
                        style={{
                            margin: 0,
                            color: "#0f172a"
                        }}
                    >
                        Email User
                    </h2>

                    <p
                        style={{
                            margin:
                                "6px 0 0",
                            color: "#64748b"
                        }}
                    >
                        Message from HalalHeart Safety Team
                    </p>

                </div>

                <button
                    type="button"
                    onClick={() =>
                        setEmailModalOpen(
                            false
                        )
                    }
                    disabled={emailSending}
                    style={{
                        width: "36px",
                        height: "36px",
                        border: "none",
                        borderRadius: "50%",
                        background: "#f1f5f9",
                        cursor: "pointer",
                        fontSize: "20px"
                    }}
                >
                    ×
                </button>

            </div>

            <div
                style={{
                    marginTop: "20px",
                    padding: "14px 16px",

                    background: "#f8fafc",

                    border:
                        "1px solid #e2e8f0",

                    borderRadius: "12px"
                }}
            >

                <div
                    style={{
                        fontSize: "11px",
                        color: "#94a3b8",
                        fontWeight: "800"
                    }}
                >
                    TO
                </div>

                <div
                    style={{
                        marginTop: "4px",
                        fontWeight: "800"
                    }}
                >
                    {user.name}
                </div>

                <div
                    style={{
                        marginTop: "3px",
                        color: "#64748b",
                        fontSize: "14px"
                    }}
                >
                    {user.username}
                </div>

            </div>

            <textarea
                value={emailBody}

                onChange={(event) =>
                    setEmailBody(
                        event.target.value
                    )
                }

                placeholder="Write your message to the user..."

                rows={8}

                autoFocus

                style={{
                    width: "100%",
                    boxSizing: "border-box",

                    marginTop: "20px",

                    padding: "15px",

                    border:
                        "1px solid #cbd5e1",

                    borderRadius: "13px",

                    resize: "vertical",

                    fontFamily: "inherit",

                    fontSize: "15px",

                    lineHeight: "1.6"
                }}
            />

            {emailSuccess && (

                <div
                    style={{
                        marginTop: "12px",
                        padding: "11px",

                        borderRadius: "10px",

                        background: "#ecfdf5",

                        color: "#15803d",

                        fontWeight: "700"
                    }}
                >
                    {emailSuccess}
                </div>

            )}

            <div
                style={{
                    display: "flex",
                    justifyContent:
                        "flex-end",
                    gap: "10px",

                    marginTop: "20px"
                }}
            >

                <button
                    type="button"

                    disabled={emailSending}

                    onClick={() =>
                        setEmailModalOpen(
                            false
                        )
                    }
                >
                    Cancel
                </button>

                <button
                    type="button"

                    disabled={
                        emailSending ||
                        !emailBody.trim()
                    }

                    onClick={sendUserEmail}

                    style={{
                        border: "none",
                        borderRadius: "10px",

                        padding:
                            "11px 20px",

                        background:
                            emailSending ||
                            !emailBody.trim()
                                ? "#f9a8d4"
                                : "#ec4899",

                        color: "#fff",

                        fontWeight: "800",

                        cursor:
                            emailSending ||
                            !emailBody.trim()
                                ? "not-allowed"
                                : "pointer"
                    }}
                >
                    {emailSending
                        ? "Sending..."
                        : "Send Email"}
                </button>

            </div>

        </div>

    </div>

)}

        </div>
    );
};


const Info = ({
    label,
    value
}) => (
    <div
        style={{
            background: "#f8fafc",
            padding: "16px",
            borderRadius: "13px",
            border:
                "1px solid #f1f5f9"
        }}
    >

        <div
            style={{
                fontSize: "12px",
                color: "#64748b",
                marginBottom: "6px"
            }}
        >
            {label}
        </div>

        <strong>
            {value ?? "-"}
        </strong>

    </div>
);


const StatusBadge = ({
    text,
    background,
    color
}) => (
    <span
        style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "5px 10px",
            borderRadius: "999px",
            background,
            color,
            fontSize: "12px",
            fontWeight: "800"
        }}
    >
        {text}
    </span>
);


const ActionButton = ({
    text,
    background,
    onClick
}) => (
    <button
        type="button"
        onClick={onClick}
        style={{
            padding: "11px 18px",
            border: "none",
            borderRadius: "10px",
            background,
            color: "#fff",
            cursor: "pointer",
            fontWeight: "700"
        }}
    >
        {text}
    </button>


);


export default AdminUserDetail;